import { LatLng } from '@porterin/booking-flow/src/order-review/domain/entities/LatLng';
import { MapPinCmpDesktop } from '@porterin/component-library/src/location-accuracy/components/MapPinCmpDesktop';
import { useLocation } from '@porterin/component-library/src/location-accuracy/contexts/LocationContext';
import {
  handleMapDragEnd,
  handleGetLocation,
  mapOptions,
} from '@porterin/component-library/src/location-accuracy/utils/LocationAccuracyHelpers';
import { GoogleMap } from '@react-google-maps/api';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapLocationAddressCmpDesktop } from './MapLocationAddressCmpDesktop';
import { captureException } from '../../ts-utils/analytics/sentry/Sentry';
import { getScreenName } from '@porterin/lead-flow/src/components/FareEstimateHelper';

const containerStyle = {
  width: '100%',
  height: '25rem',
};

export const MapLocationSelectionCmpDesktop: React.FC = () => {
  const { state, selectLocation } = useLocation();
  const [center, setCenter] = useState<LatLng>({
    lat: state.address?.location.lat ?? 12.9715987,
    lng: state.address?.location.lng ?? 77.594566,
  });
  const [isDragging, setIsDragging] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [zoom, setZoom] = useState(18);
  const hasAttemptedGeolocation = useRef(false);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      if (!state.address?.landmark && !hasAttemptedGeolocation.current) {
        hasAttemptedGeolocation.current = true;
        handleGetLocation({
          selectLocation: result => {
            selectLocation(result);
            const pos = {
              lat: result.location.lat,
              lng: result.location.lng,
            };
            setCenter(pos);
            map.panTo(pos);
          },
          waypoint: state.waypoint === 'Pickup' ? 'Pickup' : 'Drop',
          screen_name: getScreenName(),
          setError: error => {
            if (error) {
              const errorMessage = `Geolocation error: ${error instanceof Error ? error.message : String(error)}`;
              console.error(errorMessage);
              captureException(errorMessage);
            }
          },
        });
      }
    },
    [state.address?.landmark],
  );

  const onDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = useCallback(() => {
    handleMapDragEnd({
      mapRef,
      debounceRef,
      setIsDragging,
      setCenter,
      selectLocation,
    });
  }, [selectLocation]);

  const onZoomChanged = () => {
    setZoom(mapRef.current?.getZoom() ?? 18);
  };

  const handleZoomIn = useCallback(() => {
    const currentZoom = mapRef.current?.getZoom() ?? 18;
    const newZoom = Math.min(currentZoom + 1, 21);
    mapRef.current?.setZoom(newZoom);
    setZoom(newZoom);
  }, []);

  const handleZoomOut = useCallback(() => {
    const currentZoom = mapRef.current?.getZoom() ?? 18;
    const newZoom = Math.max(currentZoom - 1, 1);
    mapRef.current?.setZoom(newZoom);
    setZoom(newZoom);
  }, []);

  useEffect(() => {
    const address = state?.address;
    if (!address?.location) return;

    const timeoutId = setTimeout(() => {
      const pos = {
        lat: address.location.lat ?? state.mapCenter?.lat ?? 12.9715987,
        lng: address.location.lng ?? state.mapCenter?.lng ?? 77.594566,
      };
      setCenter(pos);
      mapRef.current?.panTo(pos);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [state.address?.location?.lat, state.address?.location?.lng]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="relative w-full h-full mt-[3rem]">
      <div className="relative w-full rounded-[1.6rem] overflow-hidden h-[24.8rem]">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={zoom}
          options={mapOptions}
          onLoad={onLoad}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onZoomChanged={onZoomChanged}
        />
        <MapPinCmpDesktop />
      </div>
      <div className="mt-[2.4rem]">
        <MapLocationAddressCmpDesktop
          is_dragging={isDragging}
          set_center={setCenter}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
        />
      </div>
    </div>
  );
};
